import { supabase } from '../supabaseClient';

// Check if the current user is an admin
export const checkIsAdmin = async (userEmail) => {
  try {
    const { data, error } = await supabase
      .from('admin_emails')
      .select('email')
      .eq('email', userEmail)
      .single();

    if (error && error.code !== 'PGRST116') { // PGRST116 is "not found" error
      console.error('Error checking admin status:', error);
      return false;
    }

    return !!data;
  } catch (error) {
    console.error('Error in checkIsAdmin:', error);
    return false;
  }
};

// Get all tickets for a user (or all tickets if admin)
export const getUserTickets = async (userId, userEmail, isAdmin = false) => {
  try {
    let query = supabase
      .from('support_tickets')
      .select(`
        *,
        ticket_messages (
          id,
          message,
          user_email,
          is_admin_note,
          created_at
        ),
        ticket_attachments (
          id,
          file_name,
          file_path,
          mime_type,
          created_at
        )
      `)
      .order('created_at', { ascending: false });

    // If not admin, only get user's own tickets
    if (!isAdmin) {
      query = query.eq('user_id', userId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching tickets:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error in getUserTickets:', error);
    return { data: null, error };
  }
};

// Create a new support ticket
export const createTicket = async (ticketData) => {
  try {
    const { data, error } = await supabase
      .from('support_tickets')
      .insert([{
        user_id: ticketData.user_id,
        user_email: ticketData.user_email,
        title: ticketData.title,
        description: ticketData.description,
        priority: ticketData.priority || 'medium',
        status: 'pending'
      }])
      .select()
      .single();

    if (error) {
      console.error('Error creating ticket:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error in createTicket:', error);
    return { data: null, error };
  }
};

// Add a message to a ticket
export const addTicketMessage = async (messageData) => {
  try {
    const { data, error } = await supabase
      .from('ticket_messages')
      .insert([{
        ticket_id: messageData.ticket_id,
        user_id: messageData.user_id,
        user_email: messageData.user_email,
        message: messageData.message,
        is_admin_note: messageData.is_admin_note || false
      }])
      .select()
      .single();

    if (error) {
      console.error('Error adding message:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error in addTicketMessage:', error);
    return { data: null, error };
  }
};

// Update ticket status (admin only)
export const updateTicketStatus = async (ticketId, status) => {
  try {
    const { data, error } = await supabase
      .from('support_tickets')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', ticketId)
      .select()
      .single();

    if (error) {
      console.error('Error updating ticket status:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error in updateTicketStatus:', error);
    return { data: null, error };
  }
};

// Get ticket details with messages and attachments
export const getTicketDetails = async (ticketId, userEmail, isAdmin = false) => {
  try {
    const { data, error } = await supabase
      .from('support_tickets')
      .select(`
        *,
        ticket_messages (
          id,
          user_id,
          user_email,
          message,
          is_admin_note,
          created_at,
          ticket_attachments (
            id,
            file_name,
            file_path,
            mime_type,
            file_size,
            created_at
          )
        ),
        ticket_attachments (
          id,
          file_name,
          file_path,
          mime_type,
          file_size,
          created_at,
          message_id
        )
      `)
      .eq('id', ticketId)
      .single();

    if (error) {
      console.error('Error fetching ticket details:', error);
      return { data: null, error };
    }

    // Filter out admin notes for non-admin users
    if (data.ticket_messages && !isAdmin) {
      data.ticket_messages = data.ticket_messages.filter(message => !message.is_admin_note);
    }

    // Sort messages by creation date
    if (data.ticket_messages) {
      data.ticket_messages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error in getTicketDetails:', error);
    return { data: null, error };
  }
};

// Upload file attachment
export const uploadAttachment = async (file, ticketId, messageId, userId) => {
  try {
    // Generate unique file path
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `${ticketId}/${fileName}`;

    // Upload file to Supabase storage
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('ticket-attachments')
      .upload(filePath, file);

    if (uploadError) {
      console.error('Error uploading file:', uploadError);
      return { data: null, error: uploadError };
    }

    // Save attachment record to database
    const { data: attachmentData, error: attachmentError } = await supabase
      .from('ticket_attachments')
      .insert([{
        ticket_id: ticketId,
        message_id: messageId,
        file_name: file.name,
        file_path: filePath,
        file_size: file.size,
        mime_type: file.type,
        uploaded_by: userId
      }])
      .select()
      .single();

    if (attachmentError) {
      console.error('Error saving attachment record:', attachmentError);
      // Clean up uploaded file if database insert fails
      await supabase.storage
        .from('ticket-attachments')
        .remove([filePath]);
      return { data: null, error: attachmentError };
    }

    return { data: attachmentData, error: null };
  } catch (error) {
    console.error('Error in uploadAttachment:', error);
    return { data: null, error };
  }
};

// Get signed URL for file download
export const getFileUrl = async (filePath) => {
  try {
    const { data, error } = await supabase.storage
      .from('ticket-attachments')
      .createSignedUrl(filePath, 3600); // 1 hour expiry

    if (error) {
      console.error('Error getting file URL:', error);
      return { data: null, error };
    }

    return { data: data.signedUrl, error: null };
  } catch (error) {
    console.error('Error in getFileUrl:', error);
    return { data: null, error };
  }
};

// Format ticket status for display
export const formatTicketStatus = (status) => {
  const statusMap = {
    pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
    in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-800' },
    reviewing: { label: 'Reviewing', color: 'bg-purple-100 text-purple-800' },
    resolved: { label: 'Resolved', color: 'bg-green-100 text-green-800' }
  };

  return statusMap[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
};

// Format priority for display
export const formatPriority = (priority) => {
  const priorityMap = {
    low: { label: 'Low', color: 'bg-gray-100 text-gray-800' },
    medium: { label: 'Medium', color: 'bg-yellow-100 text-yellow-800' },
    high: { label: 'High', color: 'bg-red-100 text-red-800' }
  };

  return priorityMap[priority] || { label: priority, color: 'bg-gray-100 text-gray-800' };
};

// Format date for display
export const formatDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = (now - date) / (1000 * 60 * 60);

  if (diffInHours < 1) {
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
  } else if (diffInHours < 24) {
    const hours = Math.floor(diffInHours);
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  } else if (diffInHours < 168) { // 7 days
    const days = Math.floor(diffInHours / 24);
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  } else {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
};

// Mark ticket as read for current user
export const markTicketAsRead = async (ticketId, userId) => {
  try {
    const { data, error } = await supabase
      .from('ticket_read_status')
      .upsert({
        ticket_id: ticketId,
        user_id: userId,
        last_read_at: new Date().toISOString()
      }, {
        onConflict: 'ticket_id,user_id'
      });

    if (error) {
      console.error('Error marking ticket as read:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error in markTicketAsRead:', error);
    return { data: null, error };
  }
};

// Get unread ticket count for user
export const getUnreadTicketCount = async (userId, userEmail, isAdmin = false) => {
  try {
    let query = supabase
      .from('support_tickets')
      .select(`
        id,
        created_at,
        updated_at,
        user_id,
        ticket_messages!inner (
          created_at,
          is_admin_note,
          user_id
        ),
        ticket_read_status!left (
          user_id,
          last_read_at
        )
      `);

    // If not admin, only get user's own tickets
    if (!isAdmin) {
      query = query.eq('user_id', userId);
    }

    const { data: tickets, error } = await query;

    if (error) {
      console.error('Error fetching unread count:', error);
      return { count: 0, error };
    }

    let unreadCount = 0;

    tickets.forEach(ticket => {
      // Find the read status for the current user (admin or regular user)
      const readStatus = ticket.ticket_read_status.find(rs => rs.user_id === userId);
      const lastReadAt = readStatus ? new Date(readStatus.last_read_at) : new Date(0);
      
      // Filter messages based on user type
      let relevantMessages = ticket.ticket_messages;
      
      if (isAdmin) {
        // Admin sees all messages (including admin notes from other admins and user messages)
        // Admin should be notified of:
        // 1. New messages from ticket owners (non-admin users)
        // 2. Admin notes from other admins
        relevantMessages = ticket.ticket_messages;
      } else {
        // Regular users only see non-admin messages
        relevantMessages = ticket.ticket_messages.filter(msg => !msg.is_admin_note);
      }
      
      // Check if there are messages newer than last read time
      const hasUnreadMessages = relevantMessages.some(message => 
        new Date(message.created_at) > lastReadAt
      );

      if (hasUnreadMessages) {
        unreadCount++;
      }
    });

    return { count: unreadCount, error: null };
  } catch (error) {
    console.error('Error in getUnreadTicketCount:', error);
    return { count: 0, error };
  }
};

// Check if a specific ticket is unread for a user
export const isTicketUnread = async (ticketId, userId, isAdmin = false) => {
  try {
    // Get the user's read status for this ticket
    const { data: readStatus } = await supabase
      .from('ticket_read_status')
      .select('last_read_at')
      .eq('ticket_id', ticketId)
      .eq('user_id', userId)
      .maybeSingle();

    const lastReadAt = readStatus ? new Date(readStatus.last_read_at) : new Date(0);

    // Get messages for this ticket
    const { data: messages } = await supabase
      .from('ticket_messages')
      .select('created_at, is_admin_note')
      .eq('ticket_id', ticketId);

    if (!messages || messages.length === 0) {
      return false;
    }

    // Filter messages based on user type
    let relevantMessages = messages;
    if (!isAdmin) {
      relevantMessages = messages.filter(msg => !msg.is_admin_note);
    }

    // Check if there are unread messages
    return relevantMessages.some(message => 
      new Date(message.created_at) > lastReadAt
    );
  } catch (error) {
    console.error('Error checking if ticket is unread:', error);
    return false;
  }
};

// Check if an email belongs to an admin user
export const isAdminEmail = async (email) => {
  try {
    // First try database lookup
    const { data, error } = await supabase
      .from('admin_emails')
      .select('email')
      .eq('email', email)
      .maybeSingle();
    
    // If database lookup works, use it
    if (!error && data !== null) {
      return true;
    }
    
    // Fallback: check against known admin email
    return email === 'jbarker442@gmail.com';
  } catch (error) {
    console.error('Error checking admin email:', error);
    // Fallback: check against known admin email
    return email === 'jbarker442@gmail.com';
  }
};

// Check if file is an image
export const isImageFile = (mimeType) => {
  return mimeType && mimeType.startsWith('image/');
};

// Validate file for upload
export const validateFile = (file) => {
  const maxSize = 10 * 1024 * 1024; // 10MB
  const allowedTypes = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'application/pdf',
    'text/plain',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ];

  if (file.size > maxSize) {
    return { valid: false, error: 'File size must be less than 10MB' };
  }

  if (!allowedTypes.includes(file.type)) {
    return { valid: false, error: 'File type not supported. Please upload images, PDFs, or documents.' };
  }

  return { valid: true, error: null };
};
